import { useTranslation } from "react-i18next";

import { LiveMonitorCard } from "components/LiveMonitorCard/LiveMonitorCard";
import { useFormatDuration } from "hooks/useFormat";
import { IMonitor } from "interfaces/monitor.interface";

type MovementTextProps = {
  movement_mean: number | null;
};

function MovementText({ movement_mean }: MovementTextProps) {
  const { t } = useTranslation();

  if (movement_mean === null) {
    return "-";
  }
  if (movement_mean > 30) {
    return t("High Movements");
  }
  if (movement_mean > 10) {
    return t("Moderate bevegelse");
  }
  if (movement_mean > 2) {
    return t("Light Movements");
  }
  return t("calm");
}

type LiveMonitorTileProps = {
  data: IMonitor;
};

export const LiveMonitorTile = ({ data }: LiveMonitorTileProps) => {
  const { t } = useTranslation();
  const formatDuration = useFormatDuration();

  const lastData = data.state_changed_at
    ? formatDuration(data.state_changed_at, new Date())
    : t("No data");

  const subject = data.subject?.identifier || t("No subject");
  const roomName = data.device.name ?? "";

  const commonProps = {
    roomName,
    subject,
    lastData,
  };

  const movement = (
    <MovementText movement_mean={data.metrics?.latest_movement_mean ?? null} />
  );

  if (data.state === "awake_in_bed") {
    return (
      <LiveMonitorCard
        {...commonProps}
        movement={movement}
        status={t("Awake in bed")}
        variant={"warning"}
      />
    );
  }

  if (data.state === "asleep") {
    return (
      <LiveMonitorCard
        {...commonProps}
        movement={movement}
        status={t("asleep")}
        variant={"success"}
      />
    );
  }

  if (data.state === "not_in_bed") {
    return (
      <LiveMonitorCard
        {...commonProps}
        status={t("Not in bed")}
        variant={"danger"}
      />
    );
  }

  if (data.state === "device_inactive") {
    return (
      <LiveMonitorCard
        {...commonProps}
        lastData={t("No data")}
        status={t("In Active")}
        variant={"neutral"}
      />
    );
  }

  if (data.state === "device_sleep_tracking_disabled") {
    return (
      <LiveMonitorCard
        {...commonProps}
        lastData={t("No data")}
        status={t("device_sleep_tracking_disabled_status_text")}
        variant={"neutral"}
      />
    );
  }

  if (data.state === "device_offline") {
    return (
      <LiveMonitorCard
        {...commonProps}
        lastData={t("No data")}
        status={t("device_offline")}
        variant={"neutral"}
      />
    );
  }

  if (data.state === "failed") {
    return (
      <LiveMonitorCard
        {...commonProps}
        lastData={t("No data")}
        status={t("No data")}
        variant={"neutral"}
      />
    );
  }

  return null;
};
